// src/pages/Calendar.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";
import {
  Calendar as RBCalendar,
  dateFnsLocalizer,
  Views,
} from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";

type EventRow = {
  id: string;
  campaign_id: string;
  title: string;
  description: string | null;
  location: string | null;
  start_at: string;      // ISO
  end_at: string | null; // ISO or null
};

type ImportantRow = {
  id: string;
  campaign_id: string;
  title: string;
  type:
    | "election_day"
    | "early_voting"
    | "finance_deadline"
    | "registration_deadline"
    | "other";
  start_at: string; // ISO
  end_at: string;   // ISO
  all_day: boolean;
  meta: any | null; // e.g., { hours_by_date: [{date, open, close}, ...] }
};

type CalEvent = {
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource: { kind: "event" | "important"; id: string; type?: ImportantRow["type"] };
};

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

const wrap: React.CSSProperties = { padding: 16 };
const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 12,
  marginBottom: 12,
};
const input: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  width: "100%",
};
const btn: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
};
const primaryBtn: React.CSSProperties = { ...btn, border: "none", background: "#0B2E5B", color: "#fff" };
const row: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr", gap: 8 };

export default function CalendarPage() {
  // 🔵 Use global campaign from top bar
  const campaignId = useCampaignId();
  const navigate = useNavigate();

  const [events, setEvents] = useState<EventRow[]>([]);
  const [important, setImportant] = useState<ImportantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Create modal state
  const [showCreate, setShowCreate] = useState(false);
  const [createKind, setCreateKind] = useState<"event" | "important">("event");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");

  // times (local datetime input strings)
  const [start, setStart] = useState<string>(""); // "YYYY-MM-DDTHH:mm"
  const [end, setEnd] = useState<string>("");     // "YYYY-MM-DDTHH:mm"

  // important props
  const [impType, setImpType] = useState<ImportantRow["type"]>("other");
  const [allDay, setAllDay] = useState<boolean>(true);
  const [hoursJson, setHoursJson] = useState<string>(""); // optional special hours JSON

  // 🔵 Load calendar data for selected campaign
  useEffect(() => {
    if (!campaignId) return;
    setLoading(true); setErr(null);

    (async () => {
      const [evRes, impRes] = await Promise.all([
        supabase
          .from("events")
          .select("id,campaign_id,title,description,location,start_at,end_at")
          .eq("campaign_id", campaignId),
        supabase
          .from("important_calendar_items")
          .select("*")
          .eq("campaign_id", campaignId),
      ]);

      if (evRes.error) { setErr(evRes.error.message); setLoading(false); return; }
      if (impRes.error) { setErr(impRes.error.message); setLoading(false); return; }

      setEvents((evRes.data ?? []) as EventRow[]);
      setImportant((impRes.data ?? []) as ImportantRow[]);
      setLoading(false);
    })();
  }, [campaignId]);

  // Expand early voting hours_by_date into individual day entries
  const calEvents: CalEvent[] = useMemo(() => {
    const out: CalEvent[] = [];

    // Campaign Events
    for (const e of events) {
      const s = new Date(e.start_at);
      const endAt = e.end_at ? new Date(e.end_at) : new Date(s.getTime() + 60 * 60 * 1000);
      out.push({
        title: e.title,
        start: s,
        end: endAt,
        allDay: false,
        resource: { kind: "event", id: e.id },
      });
    }

    // Important Dates
    for (const i of important) {
      const s = new Date(i.start_at);
      const e = new Date(i.end_at);

      if (i.type === "early_voting" && i.meta?.hours_by_date && Array.isArray(i.meta.hours_by_date)) {
        for (const d of i.meta.hours_by_date) {
          try {
            const start = new Date(`${d.date}T${d.open}`);
            const end = new Date(`${d.date}T${d.close}`);
            out.push({
              title: `${i.title} (Early Voting)`,
              start,
              end,
              allDay: false,
              resource: { kind: "important", id: i.id, type: i.type },
            });
          } catch {/* ignore bad rows */}
        }
      } else {
        out.push({
          title: prettifyImportantTitle(i),
          start: s,
          end: e,
          allDay: i.all_day,
          resource: { kind: "important", id: i.id, type: i.type },
        });
      }
    }

    return out;
  }, [events, important]);

  function prettifyImportantTitle(i: ImportantRow) {
    switch (i.type) {
      case "election_day": return i.title || "Election Day";
      case "early_voting": return i.title || "Early Voting";
      case "finance_deadline": return i.title || "Finance Deadline";
      case "registration_deadline": return i.title || "Voter Registration Deadline";
      default: return i.title || "Important Date";
    }
  }

  function openCreate(slot?: { start?: Date; end?: Date }) {
    setShowCreate(true);
    setCreateKind("event");
    setTitle("");
    setDescription("");
    setLocation("");
    if (slot?.start && slot?.end) {
      setStart(toLocalInput(slot.start));
      setEnd(toLocalInput(slot.end));
    } else {
      const now = new Date();
      const in1h = new Date(now.getTime() + 60 * 60 * 1000);
      setStart(toLocalInput(now));
      setEnd(toLocalInput(in1h));
    }
    setImpType("other");
    setAllDay(true);
    setHoursJson("");
  }

  function closeCreate() {
    setShowCreate(false);
  }

  async function submitCreate() {
    if (!campaignId || !title.trim()) return;
    try {
      if (createKind === "event") {
        const startAt = new Date(start);
        const endAt = end ? new Date(end) : new Date(startAt.getTime() + 60 * 60 * 1000);
        const { data: u } = await supabase.auth.getUser();

        // ⬇️ Insert and return the ID, then jump to Event Detail
        const { data, error } = await supabase
          .from("events")
          .insert({
            campaign_id: campaignId,
            title,
            description: description || null,
            location: location || null,
            start_at: startAt.toISOString(),
            end_at: endAt.toISOString(),
            created_by: u?.user?.id ?? null,
          })
          .select("id")
          .single();

        if (error) throw error;

        // 🚀 Go straight to Event Detail to manage QR, branding, questions, etc.
        closeCreate();
        navigate(`/events/${data!.id}`);
        return;
      } else {
        // Important date flow (stays on calendar)
        const s = allDay ? toAllDayStart(start) : new Date(start);
        const e = allDay ? toAllDayEnd(end || start) : new Date(end || start);
        let meta: any = null;
        if (impType === "early_voting" && hoursJson.trim()) {
          try { meta = JSON.parse(hoursJson); } catch { throw new Error("Invalid JSON in special hours."); }
        }
        const { data: u } = await supabase.auth.getUser();
        const { error } = await supabase.from("important_calendar_items").insert({
          campaign_id: campaignId,
          title,
          type: impType,
          start_at: s.toISOString(),
          end_at: e.toISOString(),
          all_day: allDay,
          meta,
          created_by: u?.user?.id ?? null,
        });
        if (error) throw error;

        // refresh lists (only need for important flow since we stay here)
        const [evRes, impRes] = await Promise.all([
          supabase
            .from("events")
            .select("id,campaign_id,title,description,location,start_at,end_at")
            .eq("campaign_id", campaignId),
          supabase
            .from("important_calendar_items")
            .select("*")
            .eq("campaign_id", campaignId),
        ]);
        if (!evRes.error) setEvents((evRes.data ?? []) as EventRow[]);
        if (!impRes.error) setImportant((impRes.data ?? []) as ImportantRow[]);
        closeCreate();
      }
    } catch (e: any) {
      alert(e.message ?? "Save failed");
    }
  }

  function onSelectEvent(evt: CalEvent) {
    if (evt.resource.kind === "event") {
      // ✅ use SPA navigation
      navigate(`/events/${evt.resource.id}`);
    } else {
      const info = important.find(x => x.id === evt.resource.id);
      if (info) {
        alert(
          `${prettifyImportantTitle(info)}\n` +
          `${new Date(info.start_at).toLocaleString()} → ${new Date(info.end_at).toLocaleString()}`
        );
      }
    }
  }

  const minTime = new Date(); minTime.setHours(7, 0, 0, 0);
  const maxTime = new Date(); maxTime.setHours(21, 0, 0, 0);

  return (
    <div style={wrap}>
      {/* Header */}
      <div style={{ ...card, display:"grid", gridTemplateColumns:"1fr auto", alignItems:"center", gap:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>Calendar</div>
          <div style={{ color:"#6b7280" }}>Plan events & key dates</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {/* campaign selector removed — uses top bar */}
          <button style={primaryBtn} onClick={() => openCreate()} disabled={!campaignId}>
            + Add
          </button>
        </div>
      </div>

      {err && <div style={{ ...card, borderColor:"#fecaca", background:"#fef2f2", color:"#991b1b" }}>Error: {err}</div>}

      {/* Calendar */}
      {loading ? (
        <div style={card}>Loading calendar…</div>
      ) : (
        <div style={card}>
          <RBCalendar
            localizer={localizer}
            events={calEvents}
            startAccessor="start"
            endAccessor="end"
            views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
            defaultView={Views.MONTH}
            style={{ height: 640 }}
            onSelectSlot={openCreate}
            selectable
            onSelectEvent={onSelectEvent}
            min={minTime}
            max={maxTime}
          />
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={{
          position:"fixed", inset:0, background:"rgba(0,0,0,0.3)",
          display:"grid", placeItems:"center", padding:16, zIndex:50
        }}>
          <div style={{ ...card, maxWidth: 560, width:"100%" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <div style={{ fontWeight:700 }}>Create</div>
              <button style={btn} onClick={closeCreate}>Close</button>
            </div>

            <div style={{ display:"flex", gap:8, marginBottom:8 }}>
              <label style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
                <input
                  type="radio"
                  checked={createKind === "event"}
                  onChange={() => setCreateKind("event")}
                /> Campaign Event
              </label>
              <label style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
                <input
                  type="radio"
                  checked={createKind === "important"}
                  onChange={() => setCreateKind("important")}
                /> Important Date
              </label>
            </div>

            <div style={row}>
              <input style={input} placeholder="Title *" value={title} onChange={e=>setTitle(e.target.value)} />

              {createKind === "event" ? (
                <>
                  <textarea style={input} rows={2} placeholder="Description" value={description} onChange={e=>setDescription(e.target.value)} />
                  <input style={input} placeholder="Location" value={location} onChange={e=>setLocation(e.target.value)} />
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    <input type="datetime-local" style={input} value={start} onChange={e=>setStart(e.target.value)} />
                    <input type="datetime-local" style={input} value={end} onChange={e=>setEnd(e.target.value)} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    <select style={input} value={impType} onChange={e=>setImpType(e.target.value as any)}>
                      <option value="election_day">Election Day</option>
                      <option value="early_voting">Early Voting (range)</option>
                      <option value="finance_deadline">Finance Deadline</option>
                      <option value="registration_deadline">Registration Deadline</option>
                      <option value="other">Other</option>
                    </select>
                    <label style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
                      <input type="checkbox" checked={allDay} onChange={e=>setAllDay(e.target.checked)} /> All day
                    </label>
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    <input type={allDay ? "date" : "datetime-local"} style={input} value={start} onChange={e=>setStart(e.target.value)} />
                    <input type={allDay ? "date" : "datetime-local"} style={input} value={end} onChange={e=>setEnd(e.target.value)} />
                  </div>

                  {impType === "early_voting" && (
                    <div>
                      <div style={{ fontSize:13, color:"#374151", marginTop:6, marginBottom:6 }}>
                        Special hours by date (optional JSON):
                      </div>
                      <textarea
                        rows={4}
                        style={input}
                        placeholder={`{
  "hours_by_date": [
    {"date":"2025-10-24","open":"08:00","close":"20:00"},
    {"date":"2025-10-25","open":"10:00","close":"16:00"}
  ]
}`}
                        value={hoursJson}
                        onChange={e=>setHoursJson(e.target.value)}
                      />
                    </div>
                  )}
                </>
              )}

              <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                <button style={btn} onClick={closeCreate}>Cancel</button>
                <button style={primaryBtn} onClick={submitCreate} disabled={!title.trim() || !start || !campaignId}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function toLocalInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

function toAllDayStart(input: string | Date) {
  const d = typeof input === "string" ? new Date(input) : input;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
}
function toAllDayEnd(input: string | Date) {
  const d = typeof input === "string" ? new Date(input) : input;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
}
